import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Layers, Award, ExternalLink, Github, FileText } from "lucide-react";
import { useApi, fileUrl } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { gradientFor } from "@/lib/sourceMeta";
import { useCapture } from "@/components/CaptureContext";

export default function ArchivePage() {
  const api = useApi();
  const { openCapture } = useCapture();
  const { data: entries, isLoading } = useQuery({
    queryKey: ["entries", { type: "archive" }],
    queryFn: () => api.get("/entries?type=archive&limit=60"),
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 data-testid="archive-title" className="font-serif text-3xl md:text-4xl tracking-tight">
            Archive
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The portfolio of things you've built, published, won and finished.
          </p>
        </div>
        <button
          data-testid="archive-add-button"
          onClick={openCapture}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <Plus className="h-4 w-4" /> Add work
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-3xl bg-card border border-border overflow-hidden animate-pulse">
              <div className="h-56 bg-secondary" />
              <div className="p-5 space-y-2">
                <div className="h-5 bg-secondary rounded w-2/3" />
                <div className="h-3 bg-secondary rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : entries?.length ? (
        <div data-testid="archive-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {entries.map((e) => {
            const img = fileUrl(e.image_path);
            return (
              <Link
                key={e.id}
                to={`/entry/${e.id}`}
                data-testid={`archive-card-${e.id}`}
                className="group block rounded-3xl overflow-hidden bg-card border border-border shadow-[0_10px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1 hover:shadow-xl animate-fade-in"
              >
                {img ? (
                  <img src={img} alt={e.title} loading="lazy" className="h-56 w-full object-cover" />
                ) : (
                  <div className={`h-56 w-full bg-gradient-to-br ${gradientFor(e.id)} flex items-center justify-center`}>
                    <Layers className="h-9 w-9 text-white/70" strokeWidth={1.25} />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <Award className="h-3.5 w-3.5 text-primary" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                      Achievement
                    </span>
                  </div>
                  <h3 className="mt-1.5 font-serif text-xl leading-snug line-clamp-2">{e.title}</h3>
                  {e.description && (
                    <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">{e.description}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between text-[11px] text-stone-400">
                    <span className="inline-flex items-center gap-2">
                      {(e.evidence_links || []).slice(0, 3).map((l, i) => (
                        <a
                          key={i}
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(ev) => ev.stopPropagation()}
                          className="p-1 rounded-full bg-secondary hover:bg-accent/10 transition-colors"
                          aria-label={l.label || "Evidence link"}
                        >
                          {(l.url || "").includes("github") ? (
                            <Github className="h-3.5 w-3.5" />
                          ) : (l.url || "").includes("arxiv") || (l.url || "").includes("doi") ? (
                            <FileText className="h-3.5 w-3.5" />
                          ) : (
                            <ExternalLink className="h-3.5 w-3.5" />
                          )}
                        </a>
                      ))}
                    </span>
                    <span>{timeAgo(e.created_at)}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div data-testid="archive-empty-state" className="rounded-3xl border border-dashed border-border bg-card p-14 text-center">
          <p className="text-sm text-muted-foreground">
            Nothing built yet. When a discovery becomes real work — a project, a paper, an award — turn it into an achievement.
          </p>
          <button
            data-testid="archive-empty-add-button"
            onClick={openCapture}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" /> Add work
          </button>
        </div>
      )}
    </div>
  );
}
